/**
 * Video Cleanup Service
 * 
 * Handles all API communication for AI-powered video cleanup feature.
 * Provides functions to start cleanup, poll status, toggle segments, and undo.
 */

import { getApiUrl } from '../config/api';

// ============================================================================
// Types & Interfaces
// ============================================================================

export interface CleanupConfig {
  projectId: string; // Changed from mediaItemId - backend will process ALL media items in project
  contentType?: 'podcast' | 'vlog' | 'interview' | 'tutorial' | 'presentation';
  language?: string;
  provider?: 'auto' | 'claude' | 'gemini';
  skipCaption?: boolean; // Flag to reuse existing captions
}

export interface CleanupJobResponse {
  jobId: string;
  message?: string;
}

export interface CleanupStatus {
  jobId: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  percentage: number;
  message: string;
  stage: string;
  error?: string;
  result?: {
    segmentsCreated: number;
    segmentsRemoved: number;
    timeSaved: number;
  };
}

export interface ToggleResponse {
  success: boolean;
  message: string;
}

export interface BulkToggleResponse {
  success: boolean;
  count: number;
  message: string;
}

export interface UndoResponse {
  success: boolean;
  projectId: string;
  message: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get authorization headers with JWT token
 */
function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('authToken');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
}

/**
 * Handle API errors consistently
 */
async function handleApiError(response: Response): Promise<never> {
  let errorMessage = `HTTP error! status: ${response.status}`;
  
  try {
    const errorData = await response.json();
    if (errorData.message) {
      errorMessage = errorData.message;
    }
  } catch (e) {
    // If parsing JSON fails, use default error message
  }
  
  throw new Error(errorMessage);
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Start video cleanup job
 * 
 * @param config - Cleanup configuration
 * @returns Job ID for tracking progress
 */
export async function startVideoCleanup(
  config: CleanupConfig
): Promise<CleanupJobResponse> {
  try {
    console.log("Clean up payload: ", config);
    const response = await fetch(getApiUrl('/video-cleanup/start'), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        projectId: config.projectId, // Changed from mediaItemId
        provider: config.provider || 'auto',
        contentType: config.contentType || 'podcast',
        language: config.language || 'en',
        skipCaption: config.skipCaption || false,
      }),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('❌ Failed to start cleanup:', error);
    throw error;
  }
}

/**
 * Get cleanup job status
 * 
 * @param jobId - Job ID from startVideoCleanup
 * @returns Current status and progress
 */
export async function getCleanupStatus(jobId: string): Promise<CleanupStatus> {
  try {
    const response = await fetch(getApiUrl(`/video-cleanup/status/${jobId}`), {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('❌ Failed to get cleanup status:', error);
    throw error;
  }
}

/**
 * Toggle individual segment visibility
 * 
 * @param segmentId - MediaItem ID of the segment
 * @param hidden - Whether to hide the segment
 * @returns Success response
 */
export async function toggleSegment(
  segmentId: string,
  hidden: boolean
): Promise<ToggleResponse> {
  try {
    const response = await fetch(getApiUrl('/video-cleanup/toggle'), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        segmentId,
        hidden,
      }),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('❌ Failed to toggle segment:', error);
    throw error;
  }
}

/**
 * Bulk toggle multiple segments
 * 
 * @param segmentIds - Array of MediaItem IDs
 * @param hidden - Whether to hide the segments
 * @returns Success response with count
 */
export async function bulkToggleSegments(
  segmentIds: string[],
  hidden: boolean
): Promise<BulkToggleResponse> {
  try {
    const response = await fetch(getApiUrl('/video-cleanup/bulk-toggle'), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        segmentIds,
        hidden,
      }),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('❌ Failed to bulk toggle segments:', error);
    throw error;
  }
}

/**
 * Undo entire cleanup operation
 * 
 * Restores original video and removes all cleanup segments
 * 
 * @param projectId - Project ID
 * @returns Success response
 */
export async function undoCleanup(projectId: string): Promise<UndoResponse> {
  try {
    const response = await fetch(getApiUrl(`/video-cleanup/undo/${projectId}`), {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('❌ Failed to undo cleanup:', error);
    throw error;
  }
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Poll cleanup status until complete or failed
 * 
 * @param jobId - Job ID to poll
 * @param onProgress - Callback for progress updates
 * @param pollInterval - Polling interval in ms (default: 2000)
 * @returns Final status
 */
export async function pollCleanupStatus(
  jobId: string,
  onProgress: (status: CleanupStatus) => void,
  pollInterval: number = 2000
): Promise<CleanupStatus> {
  return new Promise((resolve, reject) => {
    const intervalId = setInterval(async () => {
      try {
        const status = await getCleanupStatus(jobId);
        onProgress(status);

        if (status.status === 'completed' || status.status === 'failed') {
          clearInterval(intervalId);
          if (status.status === 'completed') {
            resolve(status);
          } else {
            reject(new Error(status.error || 'Cleanup failed'));
          }
        }
      } catch (error) {
        clearInterval(intervalId);
        reject(error);
      }
    }, pollInterval);
  });
}
